<script setup lang="ts">
import { computed, ref, useTemplateRef, nextTick } from 'vue';
import { z } from 'zod';
import { UiForm, UiFormField, UiInputField, useApi, useToaster } from '@shopexperts/shared';
import type { LoginResponse } from '@shopexperts/shared';
import { useTokenStorage } from '@/app/composables/useTokenStorage';

const emit = defineEmits<{
    (e: 'authenticated', data: LoginAuthResult): void;
    (e: 'error', message: string): void;
}>();

const props = withDefaults(defineProps<{
    showToast?: boolean;
    disabled?: boolean;
}>(), {
    showToast: true,
    disabled: false,
});

export interface LoginAuthResult {
    user: LoginResponse['user'];
    token: string;
    type: LoginResponse['type'];
}

const api = useApi();
const tokenStorage = useTokenStorage();

const isLoading = ref(false);
const isDisabled = computed(() => props.disabled || isLoading.value);

const loginModel = ref({
    email: '',
    password: '',
});

const loginSchema = z.object({
    email: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string()
            .min(1, 'Email is required')
            .email('Please enter a valid email address')
            .max(255, 'Email must be 255 characters or less')
    ),
    password: z.preprocess(
        (val) => val === undefined || val === null ? '' : val,
        z.string().min(1, 'Password is required')
    ),
});

const submitRef = useTemplateRef<HTMLButtonElement>('submitRef');
const formWrapperRef = useTemplateRef<HTMLDivElement>('formWrapperRef');

function triggerSubmit() {
    if (isDisabled.value) return;
    submitRef.value?.click();
}

async function setEmail(email: string) {
    // Use nextTick to ensure form is ready, then update the model
    await nextTick();
    loginModel.value = {
        ...loginModel.value,
        email: email,
    };
}

async function focusPassword() {
    await nextTick();
    // Query for the password input field by name
    const passwordInput = formWrapperRef.value?.querySelector<HTMLInputElement>('input[name="password"]');
    passwordInput?.focus();
}

async function onSubmit(values: { email: string; password: string }) {
    if (isDisabled.value) return;
    isLoading.value = true;
    
    try {
        const result = await api.public.auth.login({
            body: {
                email: values.email,
                password: values.password,
            },
        });
        
        // Store credentials using the token storage abstraction layer
        tokenStorage.setCredentials(result.token, result.user, result.type);
        
        emit('authenticated', {
            user: result.user,
            token: result.token,
            type: result.type,
        });
    } catch (error: any) {
        console.error('[LoginForm] Login error:', error);
        const message = error?.response?.data?.message || 'Invalid email or password.';
        emit('error', message);
        if (props.showToast) {
            const toaster = useToaster();
            toaster.create({
                title: 'Login failed',
                description: message,
                type: 'error',
            });
        }
    } finally {
        isLoading.value = false;
    }
}

const forgotPasswordHref = '/forgot-password';

defineExpose({ triggerSubmit, isLoading, setEmail, focusPassword });
</script>

<template>
    <UiForm v-model="loginModel" :schema="loginSchema" @submit="onSubmit">
        <div ref="formWrapperRef" class="flex flex-col gap-5">
            <button ref="submitRef" type="submit" class="hidden" />

            <UiFormField name="email" label="Email" type="email">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="email"
                        type="email"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isDisabled"
                        placeholder="you@example.com"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="password" label="Password" type="password">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="password"
                        type="password"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="isDisabled"
                        placeholder="Enter your password"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <div class="flex justify-end">
                <a
                    :href="forgotPasswordHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-sm underline hover:no-underline font-medium text-se-gray-700"
                >
                    Forgot password?
                </a>
            </div>
        </div>
    </UiForm>
</template>
